import { useLayoutEffect } from "react";
import { Navigate, Outlet } from "@tanstack/react-router";
import { authClient, sessionCheckUnanswered } from "@/lib/auth-client";
import { SessionUnreachable } from "@/components/SessionUnreachable";
import { setNextPath } from "@/lib/nextPath";
import { queryClient } from "@/lib/query-client";
import { activatePersistence, clearPersistedData, useCacheState } from "@/lib/persistence";
import { useChatStore } from "@/features/chat/chatStore";

export function AuthedLayout() {
  const { data: session, isPending, error, refetch } = authClient.useSession();
  const unanswered = !session?.user && sessionCheckUnanswered(error);
  const restoring = useCacheState((state) => state.restoring);
  const restoredUserId = useCacheState((state) => state.restoredUserId);
  const userId = session?.user?.id;

  // The session decides whose cache this is: the same user keeps it and
  // writes follow them; a different one starts from nothing; none clears it.
  // A layout effect, so it runs before any passive effect below it — a chat
  // subscription reads this user's cache only once it is theirs.
  useLayoutEffect(() => {
    if (isPending || restoring || unanswered) return;
    if (userId) {
      activatePersistence(queryClient, userId);
      return;
    }
    queryClient.clear();
    useChatStore.getState().resetAll();
    void clearPersistedData();
  }, [isPending, restoring, unanswered, userId]);

  // Nothing renders from this browser's cache before the session check has
  // named the user it belongs to: a signed-out or different visitor never
  // sees it. The (bounded) disk read runs alongside the check.
  if (isPending || restoring) {
    return (
      <div className="auth-shell">
        <div className="spinner" />
      </div>
    );
  }

  if (unanswered) return <SessionUnreachable onRetry={() => { void refetch(); }} />;

  if (!session?.user) {
    setNextPath(window.location.pathname);
    return <Navigate to="/login" />;
  }

  // Restored for someone else: cleared by the effect above before it renders.
  if (restoredUserId && restoredUserId !== userId) {
    return (
      <div className="auth-shell">
        <div className="spinner" />
      </div>
    );
  }

  return <Outlet />;
}
